<template>
  <v-card
    :class="['notification-item', { 'unread': !notification.is_read }]"
    variant="outlined"
    @click="handleClick"
  >
    <v-card-text>
      <div class="d-flex align-start">
        <v-avatar :color="getNotificationColor(notification.type)" size="small" class="mr-3">
          <v-icon :icon="getNotificationIcon(notification.type)" color="white"></v-icon>
        </v-avatar>

        <div class="flex-grow-1">
          <div class="d-flex justify-space-between align-start">
            <div class="flex-grow-1">
              <h4 class="notification-title mb-1">{{ notification.title }}</h4>
              <p class="notification-message mb-2">{{ notification.message }}</p>

              <div v-if="notification.type === 'REQUEST_APPROVED'" class="payment-proof-link">
                <v-chip
                  size="small"
                  color="success"
                  variant="outlined"
                  prepend-icon="mdi-receipt"
                  clickable
                  @click.stop="viewPaymentProof"
                >
                  View Payment Proof
                </v-chip>
              </div>

              <div v-if="notification.type === 'REQUEST_DECLINED' && declineReason" class="decline-reason">
                <v-alert
                  type="error"
                  variant="outlined"
                  density="compact"
                  class="mt-2"
                >
                  <strong>Reason:</strong> {{ declineReason }}
                </v-alert>
              </div>

              <p class="notification-time mt-2">{{ formatTime(notification.created_at) }}</p>
            </div>

            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn
                  icon="mdi-dots-vertical"
                  variant="text"
                  size="small"
                  v-bind="props"
                  @click.stop
                ></v-btn>
              </template>
              <v-list>
                <v-list-item v-if="!notification.is_read" @click="handleMarkRead">
                  <v-list-item-title>Mark as Read</v-list-item-title>
                </v-list-item>
                <v-list-item v-else @click="handleMarkUnread">
                  <v-list-item-title>Mark as Unread</v-list-item-title>
                </v-list-item>
                <v-list-item @click="handleDelete">
                  <v-list-item-title class="text-error">Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>

        <v-icon v-if="!notification.is_read" color="primary" size="small" class="ml-2">
          mdi-circle
        </v-icon>
      </div>
    </v-card-text>
  </v-card>

  <!-- Proof of Payment Dialog -->
  <ProofOfPaymentDialog
    v-if="selectedTransactionId"
    :show="showProofDialog"
    :transaction-id="selectedTransactionId"
    @close="showProofDialog = false; selectedTransactionId = null"
  />
</template>

<script>
import ProofOfPaymentDialog from '@/components/ProofOfPaymentDialog.vue';

export default {
  name: 'NotificationItem',
  components: {
    ProofOfPaymentDialog
  },
  props: {
    notification: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showProofDialog: false,
      selectedTransactionId: null
    };
  },
  computed: {
    declineReason() {
      if (this.notification.type === 'REQUEST_DECLINED' && this.notification.data) {
        try {
          const data = JSON.parse(this.notification.data);
          return data.declineReason;
        } catch (e) {
          return null;
        }
      }
      return null;
    }
  },
  methods: {
    handleClick() {
      this.$emit('click', this.notification);
    },
    handleMarkRead() {
      this.$emit('mark-read', this.notification.id);
    },
    handleMarkUnread() {
      // In a real implementation, you would add this method to the store
    },
    handleDelete() {
      this.$emit('delete', this.notification.id);
    },
    getNotificationColor(type) {
      const colors = {
        'NEW_REQUEST': 'blue',
        'REQUEST_APPROVED': 'green',
        'REQUEST_DECLINED': 'red'
      };
      return colors[type] || 'grey';
    },
    getNotificationIcon(type) {
      const icons = {
        'NEW_REQUEST': 'mdi-file-document-plus',
        'REQUEST_APPROVED': 'mdi-check-circle',
        'REQUEST_DECLINED': 'mdi-close-circle'
      };
      return icons[type] || 'mdi-bell';
    },
    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;

      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return 'Just now';
      if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;

      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    viewPaymentProof() {
      // Show payment proof in dialog
      // Try to get transaction_id from notification data, otherwise use expense_request_id
      let transactionId = null;
      if (this.notification.data) {
        try {
          const data = JSON.parse(this.notification.data);
          transactionId = data.transactionId || data.transaction_id;
        } catch (e) {
          // Ignore parsing errors
        }
      }
      // If not in data, try direct property
      if (!transactionId) {
        transactionId = this.notification.transactionId || this.notification.transaction_id;
      }
      // If still not found, try to get from expense_request_id (would need backend lookup)
      if (!transactionId && this.notification.expense_request_id) {
        // For now, we'll try to use the expense_request_id directly
        // In a real implementation, you might need to fetch the transaction ID
        transactionId = this.notification.expense_request_id;
      }

      if (transactionId) {
        this.selectedTransactionId = transactionId;
        this.showProofDialog = true;
      } else {
        console.error('No transaction ID found in notification');
      }
    }
  }
};
</script>

<style scoped>
.notification-item {
  cursor: pointer;
  transition: all 0.2s;
  color: rgb(var(--v-theme-on-surface));
}

.notification-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(var(--v-theme-on-surface-rgb), 0.12);
}

.notification-item.unread {
  border-left: 4px solid rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary-rgb), 0.08);
}

.notification-title {
  font-size: 16px;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  margin: 0;
}

.notification-message {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), 0.78);
  margin: 0;
  line-height: 1.4;
}

.notification-time {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.62);
  margin: 0;
}

.payment-proof-link {
  margin-top: 8px;
}

.decline-reason {
  margin-top: 8px;
}
</style>
